import React, { useState } from 'react';
import { Sequences } from './Sequences';
import { GenericTableRowInterface } from '@swivl-mono/types';
import { SequencesAnalyticsDaysCombinedInterface } from '../Helpers/DataHelpers';
import { Button, ButtonColor, ButtonWithOptions } from '@swivl-mono/ui';
import '../BotAnalyticsDashboard.scss';

interface ViewAllSequencesProps {
  rawSequencesData: GenericTableRowInterface[];
  sequencesData: SequencesAnalyticsDaysCombinedInterface;
  sequencesMenu: string;
  onMenuChange: (menuType: string, value: string) => void;
  onViewLess: () => void;
  startDay: string;
  endDay: string;
}

export const ViewAllSequences: React.FC<ViewAllSequencesProps> = ({
  rawSequencesData,
  onViewLess
}) => {
  const [selectedView, setSelectedView] = useState('All Sequences');
  const [smsMenu, setSmsMenu] = useState('Day');
  const [emailMenu, setEmailMenu] = useState('Day');
  const [callMenu, setCallMenu] = useState('Day');

  const handleViewChange = (newView: string) => {
    setSelectedView(newView);

    // Set default menu to 'Type' for table-specific views
    if (['Move Ins', 'Past Due Payments', 'Move Outs', 'Abandoned Carts'].includes(newView)) {
      setSmsMenu('Type');
      setEmailMenu('Type');
      setCallMenu('Type');
    } else {
      // Reset to 'Day' for 'All Sequences' view
      setSmsMenu('Day');
      setEmailMenu('Day');
      setCallMenu('Day');
    }
  };

  // Configuration for different view types
  const getViewConfig = (viewType: string) => {
    const configs = {
      'All Sequences': {
        titlePrefix: '',
        showTypeOption: false,
        tableId: undefined
      },
      'Move Ins': {
        titlePrefix: 'Move In ',
        showTypeOption: true,
        tableId: 'fms-plus-move-in-00'
      },
      'Past Due Payments': {
        titlePrefix: 'Past Due ',
        showTypeOption: true,
        tableId: 'fms-plus-past-due-00'
      },
      'Move Outs': {
        titlePrefix: 'Move Out ',
        showTypeOption: true,
        tableId: 'fms-plus-move-out-00'
      },
      'Abandoned Carts': {
        titlePrefix: 'Abandoned Cart ',
        showTypeOption: true,
        tableId: 'fms-plus-abandoned-carts-00'
      }
    };
    return configs[viewType] || configs['All Sequences'];
  };

  const renderSequenceView = () => {
    const config = getViewConfig(selectedView);
    const showClickTracking = selectedView !== 'All Sequences';
    const channels = [
      { filter: 'SMS', menu: smsMenu, setMenu: setSmsMenu, suffix: 'SMSes' },
      { filter: 'Email', menu: emailMenu, setMenu: setEmailMenu, suffix: 'Emails' },
      { filter: 'Call', menu: callMenu, setMenu: setCallMenu, suffix: 'Calls' }
    ];

    return (
      <div className="BotAnalyticsDashboard">
        <div className="chartBoxes topRow">
          {channels.slice(0, 2).map((channel) => (
            <Sequences
              key={channel.filter}
              sequencesData={undefined}
              rawSequencesData={rawSequencesData}
              sequencesMenu={channel.menu}
              onMenuChange={(_: string, value: string) => channel.setMenu(value)}
              channelFilter={channel.filter as 'SMS' | 'Email' | 'Call'}
              title={`${config.titlePrefix}${channel.suffix} by`}
              onViewAll={undefined}
              showTypeOption={config.showTypeOption}
              tableId={config.tableId}
              showClickSegments={showClickTracking}
            />
          ))}
        </div>
        <div className="chartBoxes">
          <Sequences
            key={channels[2].filter}
            sequencesData={undefined}
            rawSequencesData={rawSequencesData}
            sequencesMenu={channels[2].menu}
            onMenuChange={(_: string, value: string) => channels[2].setMenu(value)}
            channelFilter={channels[2].filter as 'SMS' | 'Email' | 'Call'}
            title={`${config.titlePrefix}${channels[2].suffix} by`}
            onViewAll={undefined}
            showTypeOption={config.showTypeOption}
            tableId={config.tableId}
            showClickSegments={showClickTracking}
          />
        </div>
      </div>
    );
  };

  const renderContent = () => renderSequenceView();

  return (
    <div style={{
      position: 'fixed',
      top: '80px',
      backgroundColor: '#F5F5F5',
      zIndex: 9999,
      overflowY: 'auto',
      padding: '20px'
    }}>
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: '30px',
      }}>
        <button
          onClick={onViewLess}
          style={{
            fontSize: '16px',
            fontWeight: 600,
            color: '#333',
            cursor: 'pointer',
            padding: '8px 0'
          }}
        >
          ← Back
        </button>

        <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
          <div style={{ minWidth: '160px' }}>
            <ButtonWithOptions
              color={ButtonColor.BLACK}
              buttonOptions={[
                { title: "All Sequences", name: "All Sequences", onClick: () => handleViewChange("All Sequences") },
                { title: "Move Ins", name: "Move Ins", onClick: () => handleViewChange("Move Ins") },
                { title: "Past Due Payments", name: "Past Due Payments", onClick: () => handleViewChange("Past Due Payments") },
                { title: "Move Outs", name: "Move Outs", onClick: () => handleViewChange("Move Outs") },
                { title: "Abandoned Carts", name: "Abandoned Carts", onClick: () => handleViewChange("Abandoned Carts") },
              ]}
            >
              {selectedView}
            </ButtonWithOptions>
          </div>

          <Button
            color={ButtonColor.WHITE}
            onClick={() => {
              const urlMappings = {
                'Move Ins': '/properties/page/cortex/sequences/move-ins',
                'Past Due Payments': '/properties/page/cortex/sequences/pastdue',
                'Move Outs': '/properties/page/cortex/sequences/move-outs',
                'Abandoned Carts': '/properties/page/cortex/sequences/abandoned-cart'
              };
              const url = urlMappings[selectedView] || '/properties/page/cortex/sequences';
              window.open(url, '_blank');
            }}
          >
            {selectedView === 'All Sequences'
              ? 'View & Edit Sequences'
              : `View & Edit ${selectedView.slice(0, -1)} Sequence`
            }
          </Button>
        </div>
      </div>

      <div>
        {renderContent()}
      </div>
    </div>
  );
};